// C:\Users\Admin\Desktop\teacher-ai-assistant\components\admin\PlatformAdminView.tsx

import React, { useEffect, useMemo, useState } from 'react';
import { Board, Module, Role, Tenant, UserProfile, UserStatus } from '../../types';
import { firestoreService } from '../../services/firestoreService';
import { CollapsibleSection } from '../CollapsibleSection';
import { Dropdown } from '../Dropdown';
import { FileUpload } from '../FileUpload';
import { BOARDS } from '../../constants';
import { BulkUserUpload } from './BulkUserUpload';
import { parseCsvToUsersStrict } from '../../utils/csvUtils';
import { ReportingView } from './ReportingView';
import { runService } from '../../services/runService';

type AdminTab =
  | 'create'
  | 'tenant_config'
  | 'partner_config'
  | 'reporting'
  | 'users'
  | 'lo_registry';

const USER_ROLES_CREATION: Role[] = [
  Role.TENANT_ADMIN,
  Role.TEACHER_USER,
];

export const PlatformAdminView: React.FC<{
  userProfile: UserProfile;
  tenant: Tenant;
}> = ({ userProfile, tenant }) => {
  const [activeTab, setActiveTab] = useState<AdminTab>('create');

  const [formFeedback, setFormFeedback] = useState<{
    key: string;
    type: 'success' | 'error';
    text: string;
  } | null>(null);

  const showFormFeedback = (
    key: string,
    text: string,
    type: 'success' | 'error' = 'success',
    duration = 5000
  ) => {
    setFormFeedback({ key, type, text });
    window.setTimeout(() => {
      setFormFeedback(prev => (prev?.key === key ? null : prev));
    }, duration);
  };

  // Data lists
  const [allTenants, setAllTenants] = useState<{ id: string; name: string; partnerId: string }[]>([]);
  const [allPartners, setAllPartners] = useState<{ id: string; partnerId: string; partnerName: string }[]>([]);

  // Create tab state
  const [tenantName, setTenantName] = useState('');
  const [tenantPartnerId, setTenantPartnerId] = useState('osfn-direct');

  const [newPartnerName, setNewPartnerName] = useState('');
  const [partnerEmail, setPartnerEmail] = useState('');

  const [newUserEmail, setNewUserEmail] = useState('');
  const [newUserDisplayName, setNewUserDisplayName] = useState('');
  const [newUserRole, setNewUserRole] = useState<Role>(Role.TEACHER_USER);
  const [newUserTenantId, setNewUserTenantId] = useState('');
  const [newUserPartnerId, setNewUserPartnerId] = useState('');

  const [bulkUploadTenantId, setBulkUploadTenantId] = useState<string>('');
  const [bulkUploadPartnerId, setBulkUploadPartnerId] = useState<string>(''); // unused, safe to delete if desired

  // Configure tab state
  const [selectedTenantId, setSelectedTenantId] = useState<string>('');
  const [selectedTenantData, setSelectedTenantData] = useState<Tenant | null>(null);
  const [isLoadingTenant, setIsLoadingTenant] = useState<boolean>(false);

  // Partner config (configure tab)
  const [selectedPartnerId, setSelectedPartnerId] = useState<string>('');
  const [selectedPartnerData, setSelectedPartnerData] = useState<any | null>(null);
  const [isLoadingPartner, setIsLoadingPartner] = useState<boolean>(false);
  const [partnerContactEmail, setPartnerContactEmail] = useState<string>('');
  const [partnerStatus, setPartnerStatus] = useState<string>('ACTIVE');
  
const [selectedPartnerDocId, setSelectedPartnerDocId] = useState<string>('');

  // Partner credit top-up state
  const [partnerCreditAmount, setPartnerCreditAmount] = useState<string>('');
  const [partnerCreditType, setPartnerCreditType] = useState<'paid' | 'free'>('paid');
  const [partnerCreditMemo, setPartnerCreditMemo] = useState<string>('');

  const [prompts, setPrompts] = useState<Partial<Record<Module, string>>>({});
  const [creditAmount, setCreditAmount] = useState<string>('');
  const [creditType, setCreditType] = useState<'paid' | 'free'>('paid');
  const [creditMemo, setCreditMemo] = useState<string>('');
  const [newPartnerForTenant, setNewPartnerForTenant] = useState<string>('');

  // Tenant branding
  const [tenantLogoUrl, setTenantLogoUrl] = useState<string>('');
  const [tenantLogoFile, setTenantLogoFile] = useState<File | null>(null);
  const [brandingFeedback, setBrandingFeedback] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [savingLogo, setSavingLogo] = useState<boolean>(false);

  // LO Registry
  const [loFile, setLoFile] = useState<File | null>(null);
  const [loBoard, setLoBoard] = useState<Board>(Board.CBSE);
  const [loTitle, setLoTitle] = useState<string>('');
  const [loScope, setLoScope] = useState<string>('K-12');
  const [statusMessage, setStatusMessage] = useState<string>('');
  const [statusError, setStatusError] = useState<string>('');

  // Users tab state
  const [selectedTenantForUsers, setSelectedTenantForUsers] = useState<string>('');
  const [tenantUsers, setTenantUsers] = useState<UserProfile[]>([]);
  const [tenantInvites, setTenantInvites] = useState<any[]>([]);
  const [usersLoading, setUsersLoading] = useState<boolean>(false);
  const [invitesLoading, setInvitesLoading] = useState<boolean>(false);
  // Partner-scoped Users tab state (PLATFORM_ADMIN)
  const [selectedPartnerForUsers, setSelectedPartnerForUsers] = useState<string>('');
  const [partnerUsers, setPartnerUsers] = useState<UserProfile[]>([]);
  const [partnerInvites, setPartnerInvites] = useState<any[]>([]);
  const [partnerUsersLoading, setPartnerUsersLoading] = useState<boolean>(false);
  const [partnerInvitesLoading, setPartnerInvitesLoading] = useState<boolean>(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const renderFeedback = (key: string) =>
    formFeedback?.key === key ? (
      <div
        className={`mb-4 p-3 rounded-md text-sm ${
          formFeedback.type === 'error' ? 'bg-red-100 text-red-800' : 'bg-green-100 text-green-800'
        }`}
      >
        {formFeedback.text}
      </div>
    ) : null;

  const fetchData = async () => {
    try {
      const tenants = await firestoreService.getAllTenants();
      setAllTenants(
        (tenants || []).sort((a: any, b: any) => String(a?.name || '').localeCompare(String(b?.name || '')))
      );

     const partners = await firestoreService.getAllPartners();
setAllPartners(
  (partners || []).sort((a: any, b: any) =>
    String(a?.partnerName || '').localeCompare(String(b?.partnerName || ''))
  )
);
} catch (e: any) {
  console.error('[PlatformAdminView] fetchData failed', e);
  showFormFeedback(
    'global',
    `${String(e?.code || '')} ${String(e?.message || 'Failed to load tenants/partners.')}`.trim(),
    'error'
  );
}
};

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load selected tenant data (configure)
  useEffect(() => {
    const run = async () => {
      if (!selectedTenantId) {
        setSelectedTenantData(null);
        setNewPartnerForTenant('');
        setTenantLogoUrl('');
        setTenantLogoFile(null);
        setBrandingFeedback(null);
        return;
      }

      setIsLoadingTenant(true);
      setSelectedTenantData(null);
      setPrompts({});
      setBrandingFeedback(null);

      try {
        const tenantInfo = allTenants.find(t => t.id === selectedTenantId);
        setNewPartnerForTenant(tenantInfo?.partnerId || '');

        const tenantData = await firestoreService.getTenant(selectedTenantId);
        setSelectedTenantData(tenantData);

        setPrompts((tenantData as any)?.customPrompts || {});
        setTenantLogoUrl((tenantData as any)?.tenantLogoUrl || '');
        setTenantLogoFile(null);
      } catch (e: any) {
     showFormFeedback('tenantConfig', e?.message || 'Failed to load tenant config.', 'error');
      } finally {
        setIsLoadingTenant(false);
      }
    };

    run();
  }, [selectedTenantId, allTenants]);

  // Load selected partner data (configure)
  useEffect(() => {
    const run = async () => {
if (!selectedPartnerId) {
  setSelectedPartnerData(null);
  setSelectedPartnerDocId('');
  return;
}


      setIsLoadingPartner(true);
      setSelectedPartnerData(null);

      try {
        const p = await firestoreService.getPartnerByPartnerId(selectedPartnerId);
setSelectedPartnerData(p || null);
setSelectedPartnerDocId(String(p?.id || ''));

        if (p) {
          setPartnerContactEmail(String(p.contactEmail || ''));
          setPartnerStatus(String(p.status || 'ACTIVE'));
                 }
      } catch (e: any) {
        showFormFeedback('partnerConfig', e?.message || 'Failed to load partner config.', 'error');
      } finally {
        setIsLoadingPartner(false);
      }
    };

    run();
  }, [selectedPartnerId]);

  // Load users + invitations for selected tenant (users tab)
  useEffect(() => {
    const loadUsersAndInvites = async () => {
      if (!selectedTenantForUsers) {
        setTenantUsers([]);
        setTenantInvites([]);
        return;
      }

      setUsersLoading(true);
      setInvitesLoading(true);

      try {
        const [users, invites] = await Promise.all([
          firestoreService.getUsersForTenant(selectedTenantForUsers),
          firestoreService.getInvitationsForTenant(selectedTenantForUsers),
        ]);

        setTenantUsers(Array.isArray(users) ? users : []);
        setTenantInvites(Array.isArray(invites) ? invites : []);
      } catch (e: any) {
        showFormFeedback('users', e?.message || 'Failed to load users/invitations.', 'error');
        setTenantUsers([]);
        setTenantInvites([]);
      } finally {
        setUsersLoading(false);
        setInvitesLoading(false);
      }
    };

    loadUsersAndInvites();
  }, [selectedTenantForUsers]);
  // Load users + invitations for selected partner (users tab)
  useEffect(() => {
    const loadPartnerUsersAndInvites = async () => {
      if (!selectedPartnerForUsers) {
        setPartnerUsers([]);
        setPartnerInvites([]);
        return;
      }

      setPartnerUsersLoading(true);
      setPartnerInvitesLoading(true);

      try {
        const [uRes, iRes] = await Promise.all([
          runService.adminGetPartnerUsers(selectedPartnerForUsers),
          runService.adminGetPartnerInvites(selectedPartnerForUsers),
        ]);

        setPartnerUsers(Array.isArray(uRes?.users) ? uRes.users : []);
        setPartnerInvites(Array.isArray(iRes?.invites) ? iRes.invites : []);
      } catch (e: any) {
        showFormFeedback('users', e?.message || 'Failed to load partner users/invitations.', 'error');
        setPartnerUsers([]);
        setPartnerInvites([]);
      } finally {
        setPartnerUsersLoading(false);
        setPartnerInvitesLoading(false);
      }
    };

    loadPartnerUsersAndInvites();
  }, [selectedPartnerForUsers]);
  const toggleUserStatus = async (u: UserProfile) => {
    const nextStatus = u.status === UserStatus.ACTIVE ? UserStatus.INACTIVE : UserStatus.ACTIVE;

    try {
      await firestoreService.updateUserStatus(u.uid, nextStatus);
      setTenantUsers(prev => prev.map(x => (x.uid === u.uid ? { ...x, status: nextStatus } : x)));
    } catch (e: any) {
      showFormFeedback('users', e?.message || 'Failed to update user status.', 'error');
    }
  };
  const togglePartnerUserStatus = async (u: UserProfile) => {
    const nextStatus = u.status === UserStatus.ACTIVE ? UserStatus.INACTIVE : UserStatus.ACTIVE;

    try {
      await runService.adminSetUserStatus({ uid: u.uid, status: nextStatus as any });
      setPartnerUsers(prev => prev.map(x => (x.uid === u.uid ? { ...x, status: nextStatus } : x)));
    } catch (e: any) {
      showFormFeedback('users', e?.message || 'Failed to update user status.', 'error');
    }
  };
    const revokeInvite = async (inviteIdRaw: string) => {
  const inviteId = String(inviteIdRaw || '').trim();
  if (!inviteId) return;

  try {
    await firestoreService.revokeInvitationByEmail(inviteId);
    setTenantInvites((prev) =>
      Array.isArray(prev) ? prev.filter((inv: any) => String(inv?.id || '').trim() !== inviteId) : []
    );
  } catch (e) {
    console.error('[PlatformAdminView] revokeInvite failed', e);
    alert('Failed to revoke invitation.');
  }
};
  const revokePartnerInvite = async (inviteIdRaw: string) => {
    const inviteId = String(inviteIdRaw || '').trim();
    if (!inviteId) return;

    try {
      await runService.adminRevokeInvite({ inviteId });
      setPartnerInvites(prev =>
        Array.isArray(prev)
          ? prev.filter((inv: any) => String(inv?.id || inv?.inviteId || '').trim() !== inviteId)
          : []
      );
    } catch (e: any) {
      showFormFeedback('users', (e as any)?.message || 'Failed to revoke invitation.', 'error');
    }
  };
  // ------------------------
  // Handlers: Create tab
  // ------------------------
  const handleCreateTenant = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const newTenant = await firestoreService.createTenant(tenantName, tenantPartnerId);
      showFormFeedback('createTenant', `Tenant "${newTenant.tenantName}" created successfully!`);
      setTenantName('');
      setTenantPartnerId('osfn-direct');
      await fetchData();
    } catch (error: any) {
      showFormFeedback('createTenant', error?.message || 'Failed to create tenant.', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCreatePartnerAndAdmin = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const newPartner = await firestoreService.createPartner(newPartnerName, partnerEmail);

      await firestoreService.createUser(
        {
          email: partnerEmail,
          displayName: newPartnerName,
          role: Role.PARTNER_ADMIN,
          partnerId: newPartner.partnerId,
          tenantId: undefined as any,
        },
        userProfile
      );

      showFormFeedback('createPartner', `Partner "${newPartner.partnerName}" created.`);

      setNewPartnerName('');
      setPartnerEmail('');

      await fetchData();
    } catch (error: any) {
      showFormFeedback('createPartner', error?.message || 'Failed to create partner.', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const getPartnerIdForTenant = (tenantId: string): string | null => {
    const t = allTenants.find(x => x.id === tenantId);
    const pid = (t as any)?.partnerId;
    return typeof pid === 'string' && pid.trim().length > 0 ? pid.trim() : null;
  };

  const handleCreateUser = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const role = newUserRole;
      const isTenantRole = role === Role.TENANT_ADMIN || role === Role.TEACHER_USER;

      let partnerIdToUse: string | undefined = newUserPartnerId || undefined;

      if (isTenantRole) {
        if (!newUserTenantId) {
          showFormFeedback('createUser', 'Tenant is required for this role.', 'error');
          setIsSubmitting(false);
          return;
        }

        const tenantPartnerId2 = getPartnerIdForTenant(newUserTenantId);
        if (!tenantPartnerId2) {
          showFormFeedback('createUser', 'Selected tenant has no partnerId. Fix the tenant first.', 'error');
          setIsSubmitting(false);
          return;
        }

        partnerIdToUse = tenantPartnerId2;
      }

      await firestoreService.createUser(
        {
          email: newUserEmail,
          role: newUserRole,
          tenantId: newUserTenantId || (undefined as any),
          displayName: newUserDisplayName,
          partnerId: partnerIdToUse,
        },
        userProfile
      );

      showFormFeedback('createUser', `User invitation for "${newUserEmail}" sent.`);
      setNewUserEmail('');
      setNewUserDisplayName('');
      setNewUserTenantId('');
      setNewUserRole(Role.TEACHER_USER);
      setNewUserPartnerId('');
    } catch (error: any) {
      showFormFeedback('createUser', error?.message || 'Failed to invite user.', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  // ------------------------
  // Handlers: Configure tab
  // ------------------------
  const handleSavePrompts = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedTenantId) return;

    setIsSubmitting(true);
    try {
      await firestoreService.updateTenantPrompts(selectedTenantId, prompts);
      showFormFeedback('prompts', `Prompts saved for ${selectedTenantData?.tenantName || selectedTenantId}.`);
    } catch (error: any) {
      showFormFeedback('prompts', error?.message || 'Failed to save prompts.', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };
const handleAddPartnerCredits = async (e: React.FormEvent) => {
  e.preventDefault();

  const partnerKey = String(selectedPartnerDocId || selectedPartnerId || '').trim();
  if (!partnerKey) {
    showFormFeedback('partnerCredits', 'Select a partner first.', 'error');
    return;
  }

  const amount = Math.max(0, parseInt(String(partnerCreditAmount || '0'), 10) || 0);
  if (!Number.isFinite(amount) || amount <= 0) {
    showFormFeedback('partnerCredits', 'Enter a positive credit amount.', 'error');
    return;
  }

  setIsSubmitting(true);
  try {
    const updated = await firestoreService.addPartnerCredits(
      partnerKey,
      amount,
      partnerCreditType,
      partnerCreditMemo
    );

    setSelectedPartnerData(updated || null);
    showFormFeedback('partnerCredits', `Added ${amount.toLocaleString()} partner credits.`);
    setPartnerCreditAmount('');
    setPartnerCreditMemo('');
  } catch (err: any) {
    showFormFeedback('partnerCredits', err?.message || 'Failed to add partner credits.', 'error');
  } finally {
    setIsSubmitting(false);
  }
};
  const handleAddCredits = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedTenantId) return;

    const amount = Math.max(0, parseInt(String(creditAmount || '0'), 10) || 0);
    if (!Number.isFinite(amount) || amount <= 0) {
      showFormFeedback('credits', 'Enter a positive credit amount.', 'error');
      return;
    }

    setIsSubmitting(true);
    try {
      const updatedTenant = await firestoreService.addCreditsToTenant(selectedTenantId, amount, creditType, creditMemo);
      setSelectedTenantData(updatedTenant);
      showFormFeedback('credits', `Added ${amount.toLocaleString()} credits.`);
      setCreditAmount('');
      setCreditMemo('');
    } catch (error: any) {
      showFormFeedback('credits', error?.message || 'Failed to add credits.', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handlePartnerAssignment = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedTenantId) return;

    setIsSubmitting(true);
    try {
      await firestoreService.updateTenantPartner(selectedTenantId, newPartnerForTenant);
      showFormFeedback('partnerAssign', 'Partner assignment updated.');
      await fetchData();
    } catch (error: any) {
      showFormFeedback('partnerAssign', error?.message || 'Failed to update partner assignment.', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleUploadOutcomes = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!loFile || !loTitle.trim() || !loScope.trim()) {
      showFormFeedback('lo', 'Please provide a Title, Scope, and select a PDF file.', 'error');
      return;
    }

    setIsSubmitting(true);
    setStatusMessage('');
    setStatusError('');

    try {
      const base64File: string = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onerror = () => reject(new Error('lo_file_read_failed'));
        reader.onload = () => {
          const result = String(reader.result || '');
          const commaIdx = result.indexOf(',');
          resolve(commaIdx >= 0 ? result.slice(commaIdx + 1) : result);
        };
        reader.readAsDataURL(loFile);
      });

      const runResult: any = await runService.startRun(tenant, userProfile, 'lo_parse', {
        board: loBoard,
        loFile: { content: base64File, type: loFile.type || 'application/pdf' },
      });
const timestamp = new Date();
const generatedDocId = `${String(loBoard).toLowerCase().replace(/\s/g, '-')}_${String(loScope)
  .toLowerCase()
  .replace(/\s/g, '-')}_${timestamp.getTime()}`;
const generatedVersion = timestamp.toISOString();

// Accept both response shapes: { items } or { aiResult: { items } }
const rawItems =
  (Array.isArray(runResult?.items) ? runResult.items : null) ??
  (Array.isArray(runResult?.aiResult?.items) ? runResult.aiResult.items : null) ??
  [];

const loItems = rawItems
  .filter(Boolean)
  .map((it: any, idx: number) => ({
    ...it,
    id: String(it?.id || `${generatedDocId}_${idx}`),
    board: String(it?.board || loBoard).trim().toUpperCase(),
    outcome: String(it?.outcome || '').trim(),
    subject: String(it?.subject || '').trim(),
  }))
  .filter((it: any) => Boolean(it.subject) && Boolean(it.outcome));

await firestoreService.uploadLearningOutcomes(
  loBoard,
  { doc_id: generatedDocId, version: generatedVersion, title: loTitle, scope: loScope },
  loItems
);

setStatusMessage(`Uploaded ${loItems.length} learning outcomes for ${loBoard}. Active set updated.`);
showFormFeedback('lo', `Uploaded ${loItems.length} outcomes for ${loBoard}.`, 'success');
     
      setLoFile(null);
      setLoTitle('');
      setLoScope('K-12');
    } catch (error: any) {
      const msg = error?.message || 'Failed to upload learning outcomes.';
      setStatusError(msg);
      showFormFeedback('lo', msg, 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

 // Bulk CSV upload
const processPlatformUsers = (file: File) => {
  if (!bulkUploadTenantId) {
    throw new Error('Select a target Tenant for bulk upload before uploading the CSV.');
  }

  const tenantPartnerId2 = getPartnerIdForTenant(bulkUploadTenantId);
  if (!tenantPartnerId2) {
    throw new Error('Selected tenant has no partnerId. Fix the tenant first.');
  }

  const tenantObj = allTenants.find(t => t.id === bulkUploadTenantId);
  const tenantName2 = tenantObj?.name ? String(tenantObj.name) : null;

  const expectedHeaders = ['email', 'role'];
  return parseCsvToUsersStrict(file, expectedHeaders.map(h => h.toLowerCase())).then(users => {
    const allowedRoles = new Set<string>([Role.TENANT_ADMIN, Role.TEACHER_USER]);

    const rowErrors: any[] = [];
    const rows: Array<{ email: string; role: Role }> = [];

    for (const user of users) {
      const rowNumber = (user as any)?.rowNumber;
      const role = String((user as any)?.role || '').trim().toUpperCase() as Role;
      const email = String((user as any)?.email || '').trim().toLowerCase();

      if (!email) {
        rowErrors.push({ rowNumber, email, role, error: 'row_missing_email' });
        continue;
      }
      if (!email.includes('@')) {
        rowErrors.push({ rowNumber, email, role, error: 'row_invalid_email' });
        continue;
      }
      if (!allowedRoles.has(role)) {
        rowErrors.push({ rowNumber, email, role, error: 'role_not_allowed_for_bulk' });
        continue;
      }

      rows.push({ email, role });
    }

    // Deterministic ordering
    rows.sort((a, b) => `${a.email}__${a.role}`.localeCompare(`${b.email}__${b.role}`));

    const createdByUid = String((userProfile as any)?.uid || '').trim();
    if (!createdByUid) throw new Error('missing_createdByUid');

    // If nothing valid, return rowErrors as the rejected set (no throw)
    if (!rows.length) {
      return { created: 0, skipped: 0, errors: rowErrors };
    }

    return firestoreService
      .bulkAddUsers({
        createdByUid,
        tenantId: bulkUploadTenantId,
        tenantName: tenantName2,
        partnerId: tenantPartnerId2,
        rows,
      })
      .then(res => ({ ...res, errors: [...rowErrors, ...(res?.errors || [])] }));
  });
};
  const pendingTenantInvites = tenantInvites.filter((inv: any) =>
    String(inv?.normalizedStatus || inv?.status || 'PENDING').toUpperCase() === 'PENDING'
  );

  // ------------------------
  // Render: Tabs
  // ------------------------
  const tabButtonClass = (tab: AdminTab) =>
    `px-4 py-2 rounded-md text-sm font-semibold ${
      activeTab === tab ? 'bg-brand-orange text-white' : 'bg-white text-brand-grey border border-gray-200'
    }`;

  const renderCreateTab = () => (
    <div className="space-y-6">
      <CollapsibleSection title="Create Tenant">
        <form onSubmit={handleCreateTenant} className="space-y-4 p-4">
          {renderFeedback('createTenant')}

          <div>
            <label className="block text-sm font-bold text-brand-grey mb-1">Tenant Name</label>
            <input
              type="text"
              value={tenantName}
              onChange={e => setTenantName(e.target.value)}
              placeholder="e.g., Green Valley High School"
              className="block w-full px-3 py-2 bg-white border border-gray-300 rounded-md"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-bold text-brand-grey mb-1">Assign to Partner (Optional)</label>
            <select
              value={tenantPartnerId}
              onChange={e => setTenantPartnerId(e.target.value)}
              className="block w-full px-3 py-2 bg-white border border-gray-300 rounded-md"
            >
              {allPartners.map(p => (
                <option key={p.partnerId} value={p.partnerId}>
                  {p.partnerName}
                </option>
              ))}
            </select>
          </div>

          <div className="flex justify-end">
            <button
              type="submit"
              disabled={isSubmitting}
              className="px-6 py-2 bg-brand-orange text-white font-semibold rounded-md shadow-md disabled:bg-gray-400"
            >
              Create Tenant
            </button>
          </div>
        </form>
      </CollapsibleSection>

      <CollapsibleSection title="Create Partner">
        <form onSubmit={handleCreatePartnerAndAdmin} className="space-y-4 p-4">
          {renderFeedback('createPartner')}

          <div className="space-y-4 border rounded-lg p-4">
            <h3 className="text-lg font-semibold">Create Partner</h3>

            <input
              type="text"
              placeholder="Partner Name"
              value={newPartnerName}
              onChange={e => setNewPartnerName(e.target.value)}
              className="w-full border rounded px-3 py-2"
              required
            />

            <input
              type="email"
              placeholder="Partner Email"
              value={partnerEmail}
              onChange={e => setPartnerEmail(e.target.value)}
              className="w-full border rounded px-3 py-2"
              required
            />

            <div className="flex justify-end">
              <button
                type="submit"
                disabled={isSubmitting}
                className="bg-brand-orange text-white px-4 py-2 rounded disabled:bg-gray-400"
              >
                Create Partner
              </button>
            </div>
          </div>
        </form>
      </CollapsibleSection>

      <CollapsibleSection title="Create Single User">
        <form onSubmit={handleCreateUser} className="space-y-4 p-4">
          {renderFeedback('createUser')}

          <div>
            <label className="block text-sm font-bold text-brand-grey mb-1">Email</label>
            <input
              type="email"
              value={newUserEmail}
              onChange={e => setNewUserEmail(e.target.value)}
              className="block w-full px-3 py-2 bg-white border border-gray-300 rounded-md"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-bold text-brand-grey mb-1">Display Name</label>
            <input
              type="text"
              value={newUserDisplayName}
              onChange={e => setNewUserDisplayName(e.target.value)}
              className="block w-full px-3 py-2 bg-white border border-gray-300 rounded-md"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-bold text-brand-grey mb-1">Role</label>
            <select
              value={newUserRole}
              onChange={e => setNewUserRole(e.target.value as Role)}
              className="block w-full px-3 py-2 bg-white border border-gray-300 rounded-md"
            >
              {USER_ROLES_CREATION.map(r => (
                <option key={r} value={r}>
                  {r}
                </option>
              ))}
            </select>
          </div>

          {newUserRole === Role.PARTNER_ADMIN && (
            <div>
              <label className="block text-sm font-bold text-brand-grey mb-1">Partner ID</label>
              <select
                value={newUserPartnerId}
                onChange={e => setNewUserPartnerId(e.target.value)}
                className="block w-full px-3 py-2 bg-white border border-gray-300 rounded-md"
                required
              >
                <option value="">Select Partner</option>
                {allPartners.map(p => (
                  <option key={p.partnerId} value={p.partnerId}>
                    {p.partnerName}
                  </option>
                ))}
              </select>
            </div>
          )}

          <div>
            <label className="block text-sm font-bold text-brand-grey mb-1">Tenant</label>
            <select
              value={newUserTenantId}
              onChange={e => setNewUserTenantId(e.target.value)}
              className="block w-full px-3 py-2 bg-white border border-gray-300 rounded-md"
              required
            >
              <option value="">Select Tenant</option>
            {allTenants.map((t: any) => (
  <option key={t.id} value={t.id}>
    {String(t?.name || t?.tenantName || '').trim() || t.id}
  </option>
))}
            </select>
          </div>

          <div className="flex justify-end">
            <button
              type="submit"
              disabled={isSubmitting}
              className="px-6 py-2 bg-brand-orange text-white font-semibold rounded-md shadow-md disabled:bg-gray-400"
            >
              Create User
            </button>
          </div>
        </form>
      </CollapsibleSection>

      <CollapsibleSection title="Bulk User Upload">
        <div className="space-y-3 p-4">
          <div>
            <label className="block text-sm font-bold text-brand-grey mb-1">Target Tenant (required)</label>
            <select
              value={bulkUploadTenantId}
              onChange={e => setBulkUploadTenantId(e.target.value)}
              className="block w-full px-3 py-2 bg-white border border-gray-300 rounded-md"
              required
            >
              <option value="">Select Tenant</option>
              {allTenants.map(t => (
                <option key={t.id} value={t.id}>
                  {t.name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-bold text-brand-grey mb-1">Partner (Auto from Tenant)</label>
            <select
              value={
                bulkUploadTenantId
                  ? getPartnerIdForTenant(bulkUploadTenantId) || 'osfn-direct'
                  : 'osfn-direct'
              }
              disabled
              className="block w-full px-3 py-2 bg-white border border-gray-300 rounded-md"
            >
              {allPartners.map(p => (
                <option key={p.partnerId} value={p.partnerId}>
                  {p.partnerName}
                </option>
              ))}
            </select>
          </div>

          <BulkUserUpload
            templateHeaders={['email', 'role']}
            templateFilename="platform_user_template.csv"
            processAndUpload={processPlatformUsers}
          />
        </div>
      </CollapsibleSection>
    </div>
  );
const renderTenantConfigTab = () => (
  <div className="space-y-6">
    {/* Tenant selector only */}
    <div className="bg-white p-4 rounded-lg shadow-sm border">
     {renderFeedback('tenantConfig')}
      <label htmlFor="tenant-select" className="block text-sm font-bold text-brand-grey mb-1">
        Configure Tenant
      </label>
      <select
        id="tenant-select"
        value={selectedTenantId}
        onChange={e => setSelectedTenantId(e.target.value)}
        className="block w-full px-3 py-2 bg-white border border-gray-300 rounded-md"
      >
        <option value="">-- Select a Tenant --</option>
        {allTenants.map(t => (
          <option key={t.id} value={t.id}>
            {t.name}
          </option>
        ))}
      </select>
    </div>

    {isLoadingTenant ? (
      <p>Loading...</p>
    ) : selectedTenantData ? (
      <div className="space-y-6">
        <CollapsibleSection title="Assign Partner">
          <form onSubmit={handlePartnerAssignment} className="space-y-4 p-4">
            {renderFeedback('partnerAssign')}

            <div>
              <label className="block text-sm font-bold text-brand-grey mb-1">
                Assign "{selectedTenantData.tenantName}" to Partner
              </label>
              <select
                value={newPartnerForTenant}
                onChange={e => setNewPartnerForTenant(e.target.value)}
                className="block w-full px-3 py-2 bg-white border border-gray-300 rounded-md"
              >
                <option value="">None</option>
                {allPartners.map(p => (
                  <option key={p.partnerId} value={p.partnerId}>
                    {p.partnerName}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex justify-end">
              <button
                type="submit"
                disabled={isSubmitting}
                className="px-6 py-2 bg-brand-orange text-white font-semibold rounded-md shadow-md disabled:bg-gray-400"
              >
                Update Assignment
              </button>
            </div>
          </form>
        </CollapsibleSection>

        <CollapsibleSection title="Tenant Branding: Logo" defaultOpen={true}>
          <div className="p-4 space-y-3">
            <label className="block text-sm font-bold text-brand-grey mb-1">Upload Tenant Logo</label>

            <input
              type="file"
              accept="image/*"
              className="w-full border px-3 py-2 rounded-md"
              disabled={!selectedTenantId || savingLogo}
              onChange={e => {
                const f = e.target.files?.[0] || null;
                setTenantLogoFile(f);
                setBrandingFeedback(null);
              }}
            />

            {tenantLogoUrl ? <div className="text-xs text-gray-600 break-all">Current URL: {tenantLogoUrl}</div> : null}

            {brandingFeedback ? (
              <div className={`text-sm ${brandingFeedback.type === 'success' ? 'text-green-700' : 'text-red-700'}`}>
                {brandingFeedback.text}
              </div>
            ) : null}

            <div className="flex justify-end">
              <button
                type="button"
                className="px-4 py-2 bg-brand-orange text-white text-sm font-semibold rounded-md shadow-sm hover:bg-opacity-90 disabled:bg-gray-300"
                disabled={!selectedTenantId || savingLogo}
                onClick={async () => {
                  if (!selectedTenantId) return;

                  setSavingLogo(true);
                  setBrandingFeedback(null);

                  try {
                    if (!tenantLogoFile) throw new Error('Select an image file first.');
                    const url = await firestoreService.uploadTenantLogoFile(selectedTenantId, tenantLogoFile);
                    await firestoreService.updateTenantLogoUrl(selectedTenantId, url);

                    setTenantLogoUrl(url);
                    setBrandingFeedback({ type: 'success', text: 'Tenant logo uploaded and saved.' });
                  } catch (e: any) {
                    setBrandingFeedback({ type: 'error', text: e?.message || 'Failed to upload tenant logo.' });
                  } finally {
                    setSavingLogo(false);
                  }
                }}
              >
                Save Logo
              </button>
            </div>
          </div>
        </CollapsibleSection>

        <CollapsibleSection title="Credit Management">
          <form onSubmit={handleAddCredits} className="space-y-4 p-4">
            {renderFeedback('credits')}

            <div>
              <label className="block text-sm font-bold text-brand-grey mb-1">Credit Amount</label>
              <input
                type="number"
                value={creditAmount}
                onChange={e => setCreditAmount(e.target.value)}
                className="block w-full px-3 py-2 bg-white border border-gray-300 rounded-md"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-bold text-brand-grey mb-1">Credit Type</label>
              <select
                value={creditType}
                onChange={e => setCreditType(e.target.value as 'paid' | 'free')}
                className="block w-full px-3 py-2 bg-white border border-gray-300 rounded-md"
              >
                <option value="paid">Paid</option>
                <option value="free">Free</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-bold text-brand-grey mb-1">Memo (optional)</label>
              <input
                type="text"
                value={creditMemo}
                onChange={e => setCreditMemo(e.target.value)}
                className="block w-full px-3 py-2 bg-white border border-gray-300 rounded-md"
              />
            </div>

            <div className="flex justify-end">
              <button
                type="submit"
                disabled={isSubmitting}
                className="px-6 py-2 bg-brand-orange text-white font-semibold rounded-md shadow-md disabled:bg-gray-400"
              >
                Add Credits
              </button>
            </div>
          </form>
        </CollapsibleSection>

        <CollapsibleSection title="Manage Tenant Prompts">
          <form onSubmit={handleSavePrompts} className="space-y-4 p-4">
            {renderFeedback('prompts')}

            {(['atypical', 'shapeshifter', 'practice', 'assessor'] as Module[]).map(m => (
              <div key={m}>
                <label className="block text-sm font-bold text-brand-grey mb-1 capitalize">{m} Prompt</label>
                <textarea
                  value={prompts[m] || ''}
                  onChange={e => setPrompts(p => ({ ...p, [m]: e.target.value }))}
                  rows={6}
                  className="block w-full text-xs font-mono px-3 py-2 bg-white border border-gray-300 rounded-md"
                />
              </div>
            ))}

            <div className="flex justify-end">
              <button
                type="submit"
                disabled={isSubmitting}
                className="px-6 py-2 bg-brand-orange text-white font-semibold rounded-md shadow-md disabled:bg-gray-400"
              >
                Save Prompts
              </button>
            </div>
          </form>
        </CollapsibleSection>
      </div>
    ) : (
      <p className="text-center text-gray-500 py-4">Select a tenant to configure.</p>
    )}
  </div>
);

const renderPartnerConfigTab = () => (
  <div className="space-y-6">
    <div className="bg-white p-4 rounded-lg shadow-sm border space-y-3">
      {renderFeedback('partnerConfig')}

      <label className="block text-sm font-bold text-brand-grey mb-1">Configure Partner</label>
      <select
        value={selectedPartnerId}
        onChange={e => setSelectedPartnerId(e.target.value)}
        className="block w-full px-3 py-2 bg-white border border-gray-300 rounded-md"
      >
        <option value="">-- Select a Partner --</option>
        {allPartners.map(p => (
          <option key={p.partnerId} value={p.partnerId}>
            {p.partnerName} ({p.partnerId})
          </option>
        ))}
      </select>

      {isLoadingPartner ? (
        <div className="text-sm text-gray-600">Loading partner...</div>
      ) : selectedPartnerData ? (
        <div className="space-y-6">
          {/* Partner config */}
          <form
            onSubmit={async (e) => {
              e.preventDefault();
              if (!selectedPartnerId) return;

             try {
  const partnerDocIdToUse = String(selectedPartnerDocId || '').trim();
  if (!partnerDocIdToUse) {
    throw new Error('partner_doc_id_missing');
  }

  await runService.adminUpdatePartnerConfig({
 partnerDocId: String(selectedPartnerDocId || ''),
  contactEmail: partnerContactEmail,
  status: (String(partnerStatus || '').toUpperCase() as any),
});

  showFormFeedback('partnerConfig', 'Partner configuration updated.');
  const refreshed = await firestoreService.getPartnerByPartnerId(selectedPartnerId);
  setSelectedPartnerData(refreshed || null);
} catch (err: any) {
                showFormFeedback('partnerConfig', err?.message || 'Failed to update partner.', 'error');
              }
            }}
            className="space-y-4 border rounded p-4"
          >
            <div className="text-xs text-gray-600">
              <div>
                <span className="font-semibold">Partner:</span> {String(selectedPartnerData.partnerName || '')}
              </div>
              <div>
                <span className="font-semibold">Partner ID:</span>{' '}
                {String(selectedPartnerData.partnerId || selectedPartnerId)}
              </div>
              <div>
                <span className="font-semibold">Credit Balance:</span>{' '}
                free {Number(selectedPartnerData?.creditBalance?.freeRemaining || 0).toLocaleString()},{' '}
                paid {Number(selectedPartnerData?.creditBalance?.paidRemaining || 0).toLocaleString()}
              </div>
            </div>

            <div>
              <label className="block text-sm font-bold mb-1">Contact Email</label>
              <input
                type="email"
                value={partnerContactEmail}
                onChange={e => setPartnerContactEmail(e.target.value)}
                className="w-full border px-3 py-2 rounded"
              />
            </div>

            <div>
              <label className="block text-sm font-bold mb-1">Status</label>
              <select
                value={partnerStatus}
                onChange={e => setPartnerStatus(e.target.value)}
                className="w-full border px-3 py-2 rounded"
              >
                <option value="ACTIVE">ACTIVE</option>
                <option value="INACTIVE">INACTIVE</option>
              </select>
            </div>

            <div className="flex justify-end">
              <button
                type="submit"
                disabled={isSubmitting}
                className="px-4 py-2 bg-brand-orange text-white rounded disabled:bg-gray-400"
              >
                Save Partner Config
              </button>
            </div>
          </form>

          {/* Partner credit top-up (EXPLICIT) */}
          <CollapsibleSection title="Partner Credit Top-Up" defaultOpen={false}>
            <form onSubmit={handleAddPartnerCredits} className="space-y-4 p-4 border rounded">
              {renderFeedback('partnerCredits')}

              <div>
                <label className="block text-sm font-bold text-brand-grey mb-1">Credit Amount</label>
                <input
                  type="number"
                  value={partnerCreditAmount}
                  onChange={e => setPartnerCreditAmount(e.target.value)}
                  className="block w-full px-3 py-2 bg-white border border-gray-300 rounded-md"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-bold text-brand-grey mb-1">Credit Type</label>
                <select
                  value={partnerCreditType}
                  onChange={e => setPartnerCreditType(e.target.value as 'paid' | 'free')}
                  className="block w-full px-3 py-2 bg-white border border-gray-300 rounded-md"
                >
                  <option value="paid">Paid</option>
                  <option value="free">Free</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-bold text-brand-grey mb-1">Memo (optional)</label>
                <input
                  type="text"
                  value={partnerCreditMemo}
                  onChange={e => setPartnerCreditMemo(e.target.value)}
                  className="block w-full px-3 py-2 bg-white border border-gray-300 rounded-md"
                />
              </div>

              <div className="flex justify-end">
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="px-6 py-2 bg-brand-orange text-white font-semibold rounded-md shadow-md disabled:bg-gray-400"
                >
                  Add Partner Credits
                </button>
              </div>
            </form>
          </CollapsibleSection>
        </div>
      ) : (
        <div className="text-sm text-gray-500">Select a partner to view config.</div>
      )}
    </div>
  </div>
);
const renderLoRegistryTab = () => (
  <div className="space-y-6">
    <CollapsibleSection title="Learning Outcomes Registry" defaultOpen={true}>
      <form onSubmit={handleUploadOutcomes} className="space-y-4 p-4">
        {renderFeedback('lo')}

        {statusMessage ? (
          <div className="p-3 rounded-lg bg-green-50 text-green-800 text-sm">{statusMessage}</div>
        ) : null}

        {statusError ? <div className="p-3 rounded-lg bg-red-50 text-red-800 text-sm">{statusError}</div> : null}

        <p className="text-sm text-gray-600">
          Upload a new curriculum document. This will become the active set of learning outcomes for the selected board.
          Document ID and Version will be auto-generated.
        </p>

        <Dropdown
          label="Board"
          options={BOARDS}
          selectedValue={loBoard}
          onValueChange={v => setLoBoard(v as Board)}
          id="lo-board"
        />

        <div>
          <label className="block text-sm font-bold text-brand-grey mb-1">Title</label>
          <input
            type="text"
            value={loTitle}
            onChange={e => setLoTitle(e.target.value)}
            className="block w-full px-3 py-2 bg-white border border-gray-300 rounded-md"
            required
          />
        </div>

        <div>
          <label className="block text-sm font-bold text-brand-grey mb-1">Scope</label>
          <input
            type="text"
            value={loScope}
            onChange={e => setLoScope(e.target.value)}
            className="block w-full px-3 py-2 bg-white border border-gray-300 rounded-md"
            required
          />
        </div>

        <FileUpload label="PDF File" accept=".pdf" onFileChange={f => setLoFile(f[0] || null)} />

        <div className="flex justify-end">
          <button
            type="submit"
            disabled={isSubmitting || !loFile}
            className="px-6 py-2 bg-brand-orange text-white font-semibold rounded-md shadow-md disabled:bg-gray-400"
          >
            Upload Registry
          </button>
        </div>
      </form>
    </CollapsibleSection>
  </div>
);

    const pendingPartnerInvites = partnerInvites.filter((inv: any) =>
    String(inv?.normalizedStatus || inv?.status || 'PENDING').toUpperCase() === 'PENDING'
  );

  const renderUsersTab = () => (
    <div className="space-y-4">
      <div className="bg-white p-6 rounded-xl shadow-lg space-y-4">
        <h2 className="text-xl font-bold">User Management</h2>

        {renderFeedback('users')}

        <CollapsibleSection title="Tenants" defaultOpen={true}>
          <div className="space-y-4 p-4">
            <div>
              <label className="block text-sm font-bold text-brand-grey mb-1">Tenant</label>
              <select
                className="block w-full px-3 py-2 border border-gray-300 rounded-md"
                value={selectedTenantForUsers}
                onChange={e => setSelectedTenantForUsers(e.target.value)}
              >
                <option value="">-- Select Tenant --</option>
                {allTenants.map(t => (
                  <option key={t.id} value={t.id}>
                    {t.name} ({t.id})
                  </option>
                ))}
              </select>
            </div>

            {usersLoading || invitesLoading ? (
              <p>Loading...</p>
            ) : (
              <div className="space-y-8">
                <div className="overflow-x-auto">
                  <h3 className="text-lg font-bold mb-3">Users</h3>

                  {tenantUsers.length === 0 && selectedTenantForUsers ? (
                    <p className="text-gray-500">No users found.</p>
                  ) : tenantUsers.length > 0 ? (
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Role</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                          <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Action</th>
                        </tr>
                      </thead>

                      <tbody className="bg-white divide-y divide-gray-200">
                        {tenantUsers.map(u => (
                          <tr key={u.uid}>
                            <td className="px-4 py-4 text-sm">{u.email}</td>
                            <td className="px-4 py-4 text-sm">{u.role}</td>
                            <td className="px-4 py-4 text-sm">{u.status}</td>
                            <td className="px-4 py-4 text-sm text-right">
                              <button
                                type="button"
                                className={`px-3 py-1 rounded text-white ${
                                  u.status === UserStatus.ACTIVE ? 'bg-red-600' : 'bg-green-600'
                                }`}
                                onClick={() => toggleUserStatus(u)}
                              >
                                {u.status === UserStatus.ACTIVE ? 'Deactivate' : 'Activate'}
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  ) : null}
                </div>

                <div className="overflow-x-auto">
                  <h3 className="text-lg font-bold mb-3">Pending Invitations</h3>

                  {pendingTenantInvites.length === 0 && selectedTenantForUsers ? (
                    <p className="text-gray-500">No pending invitations.</p>
                  ) : pendingTenantInvites.length > 0 ? (
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Role</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                          <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Action</th>
                        </tr>
                      </thead>

                      <tbody className="bg-white divide-y divide-gray-200">
                        {pendingTenantInvites.map((inv: any) => {
                          const inviteId = String(inv?.id || '').trim();
                          const email = String(inv?.email || '').trim();
                          const role = String(inv?.role || '');
                          const status = String(inv?.normalizedStatus || inv?.status || 'PENDING').toUpperCase();

                          return (
                            <tr key={inviteId || email}>
                              <td className="px-4 py-4 text-sm">{email}</td>
                              <td className="px-4 py-4 text-sm">{role}</td>
                              <td className="px-4 py-4 text-sm">{status}</td>
                              <td className="px-4 py-4 text-sm text-right">
                                <button
                                  type="button"
                                  className="px-3 py-1 rounded text-white bg-red-600"
                                  onClick={() => revokeInvite(String(inv?.id || ''))}
                                  disabled={!inviteId}
                                >
                                  Revoke
                                </button>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  ) : null}
                </div>
              </div>
            )}
          </div>
        </CollapsibleSection>

        <CollapsibleSection title="Partners" defaultOpen={false}>
          <div className="space-y-4 p-4">
            <div>
              <label className="block text-sm font-bold text-brand-grey mb-1">Partner</label>
              <select
                className="block w-full px-3 py-2 border border-gray-300 rounded-md"
                value={selectedPartnerForUsers}
                onChange={e => setSelectedPartnerForUsers(e.target.value)}
              >
                <option value="">-- Select Partner --</option>
                {allPartners.map(p => (
                  <option key={p.partnerId} value={p.partnerId}>
                    {p.partnerName} ({p.partnerId})
                  </option>
                ))}
              </select>
            </div>

            {partnerUsersLoading || partnerInvitesLoading ? (
              <p>Loading...</p>
            ) : (
              <div className="space-y-8">
                <div className="overflow-x-auto">
                  <h3 className="text-lg font-bold mb-3">Users</h3>

                  {partnerUsers.length === 0 && selectedPartnerForUsers ? (
                    <p className="text-gray-500">No users found.</p>
                  ) : partnerUsers.length > 0 ? (
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Role</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                          <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Action</th>
                        </tr>
                      </thead>

                      <tbody className="bg-white divide-y divide-gray-200">
                        {partnerUsers.map(u => (
                          <tr key={u.uid}>
                            <td className="px-4 py-4 text-sm">{u.email}</td>
                            <td className="px-4 py-4 text-sm">{u.role}</td>
                            <td className="px-4 py-4 text-sm">{u.status}</td>
                            <td className="px-4 py-4 text-sm text-right">
                              <button
                                type="button"
                                className={`px-3 py-1 rounded text-white ${
                                  u.status === UserStatus.ACTIVE ? 'bg-red-600' : 'bg-green-600'
                                }`}
                                onClick={() => togglePartnerUserStatus(u)}
                              >
                                {u.status === UserStatus.ACTIVE ? 'Deactivate' : 'Activate'}
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  ) : null}
                </div>

                <div className="overflow-x-auto">
                  <h3 className="text-lg font-bold mb-3">Pending Invitations</h3>

                  {pendingPartnerInvites.length === 0 && selectedPartnerForUsers ? (
                    <p className="text-gray-500">No pending invitations.</p>
                  ) : pendingPartnerInvites.length > 0 ? (
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Role</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                          <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Action</th>
                        </tr>
                      </thead>

                      <tbody className="bg-white divide-y divide-gray-200">
                        {pendingPartnerInvites.map((inv: any) => {
                          const inviteId = String(inv?.id || inv?.inviteId || '').trim();
                          const email = String(inv?.email || '').trim();
                          const role = String(inv?.role || '');
                          const status = String(inv?.normalizedStatus || inv?.status || 'PENDING').toUpperCase();

                          return (
                            <tr key={inviteId || email}>
                              <td className="px-4 py-4 text-sm">{email}</td>
                              <td className="px-4 py-4 text-sm">{role}</td>
                              <td className="px-4 py-4 text-sm">{status}</td>
                              <td className="px-4 py-4 text-sm text-right">
                                <button
                                  type="button"
                                  className="px-3 py-1 rounded text-white bg-red-600"
                                  onClick={() => revokePartnerInvite(inviteId)}
                                  disabled={!inviteId}
                                >
                                  Revoke
                                </button>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  ) : null}
                </div>
              </div>
            )}
          </div>
        </CollapsibleSection>
      </div>
    </div>
  );

  return (
    <div className="space-y-6">
      {renderFeedback('global')}

     <div className="mb-6 flex border-b-2 border-gray-200">
  {(
    ['create', 'tenant_config', 'partner_config', 'reporting', 'users', 'lo_registry'] as AdminTab[]
  ).map(tab => (
    <button
      key={tab}
      type="button"
      onClick={() => setActiveTab(tab)}
      className={`px-6 py-2 text-lg font-semibold capitalize transition-colors ${
        activeTab === tab
          ? 'border-b-2 border-brand-orange text-brand-orange'
          : 'text-gray-500 hover:text-brand-grey'
      }`}
    >
      {tab === 'tenant_config'
        ? 'Configure Tenants'
        : tab === 'partner_config'
        ? 'Configure Partners'
        : tab === 'lo_registry'
        ? 'LO Registry'
        : tab === 'users'
        ? 'User Management'
        : tab}
    </button>
  ))}
</div>

  {activeTab === 'create' && renderCreateTab()}
{activeTab === 'tenant_config' && renderTenantConfigTab()}
{activeTab === 'partner_config' && renderPartnerConfigTab()}
{activeTab === 'reporting' && <ReportingView userProfile={userProfile} />}
{activeTab === 'users' && renderUsersTab()}
{activeTab === 'lo_registry' && renderLoRegistryTab()}
    </div>
  );
};

